import { useTranslation } from 'react-i18next';
import { isTecalor } from '@common/brand/brandConfig';
import Box from '@common/components/Box';
import {
  deviceRegistration,
  isgRegistration,
  toolbox,
  userManagement,
} from '@common/components/PageHeader/constants';
import {
  isChinaDeployment,
  resolveToolboxUrl,
} from '@common/components/PageHeader/resolveToolboxUrl';
import { useAppSelector } from '@common/store';
import classnames from '@common/utils/ui/classnames';

interface AppPillProps {
  label: string;
  isActive: boolean;
  href?: string;
  testId: string;
  /** Open in a new tab with secure rel — used for the external Toolbox link. */
  external?: boolean;
  /** Hide on small screens — used for the app-switch pills (unusable on mobile). */
  hideOnMobile?: boolean;
  /** Carries the app's brand title (e.g. "tecalor Pro") — keeps the title's casing. */
  brandTitle?: boolean;
}

function AppPill({
  label,
  isActive,
  href,
  testId,
  external = false,
  hideOnMobile = false,
  brandTitle = false,
}: Readonly<AppPillProps>) {
  const className = classnames({
    'page-header__pill': true,
    'page-header__pill--active': isActive,
    'page-header__pill--brand-title': brandTitle,
    'hide-screen-small': hideOnMobile,
  });

  if (isActive) {
    return (
      <span className={className} aria-current="page" data-testid={testId}>
        {label}
      </span>
    );
  }
  return (
    <a
      className={className}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      data-testid={testId}
    >
      {label}
    </a>
  );
}

interface HeaderLinksProps {
  serviceWeltLabel: string;
  /** Whether the ServiceWelt / User management switch pills are shown (both or neither). */
  showsAppSwitch: boolean;
}

function HeaderLinks(
  { serviceWeltLabel, showsAppSwitch }: Readonly<HeaderLinksProps>,
) {
  const user = useAppSelector((state) => state.main.user);
  const deviceRegistrationType = useAppSelector((state) => state.main.deviceRegistrationType);
  // useSuspense: false is required — PageHeader also renders inside Application's Suspense
  // fallback, and a suspending fallback has no boundary above it to catch it.
  const { t, ready } = useTranslation(undefined, { useSuspense: false });
  const oppositeAppUrl = process.env.OPPOSITE_APP_URL;

  const toolboxUrl = resolveToolboxUrl({
    language: user?.language,
    countryCode: user?.countryCode,
    isTecalor,
    isChinaDeployment: isChinaDeployment(),
  });

  const isDeviceFlow = deviceRegistrationType === 'device';
  // The English constants double as the value shown until translations finish loading.
  const registrationFallback = isDeviceFlow ? deviceRegistration : isgRegistration;
  const registrationLabel = ready
    ? t(isDeviceFlow ? 'registration.header.device' : 'registration.header.isg', {
      defaultValue: registrationFallback,
    })
    : registrationFallback;

  // The switch pills appear only when the user can reach the other app; otherwise the
  // current app shows as a plain title (rendered by ApplicationSwitcher). The Toolbox
  // pill is always visible, on every view.
  return (
    <nav className="page-header__links" aria-label="Applications">
      {!deviceRegistrationType && showsAppSwitch && (
        <>
          <AppPill
            label={serviceWeltLabel}
            isActive={APP_TYPE === 'sw'}
            href={oppositeAppUrl}
            testId="sw-link"
            hideOnMobile
            brandTitle
          />
          <AppPill
            label={userManagement}
            isActive={APP_TYPE === 'um'}
            href={oppositeAppUrl}
            testId="um-link"
            hideOnMobile
          />
        </>
      )}
      {deviceRegistrationType
        ? (
          <Box my={2} tagName="span">
            <AppPill
              label={registrationLabel}
              testId="registration-readonly-link"
              isActive
            />
          </Box>
        )
        : (
          <AppPill
            label={toolbox}
            isActive={false}
            href={toolboxUrl}
            testId="toolbox-link"
            external
          />
        )}
    </nav>
  );
}

export default HeaderLinks;
